import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface TimezoneContextType {
  timezone: string;
  setTimezone: (timezone: string) => void;
  availableTimezones: { value: string; label: string }[];
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC - Coordinated Universal Time' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Zurich (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
];

interface TimezoneProviderProps {
  children: ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  // Initialize with browser's timezone or UTC fallback
  const [timezone, setTimezoneState] = useState<string>(() => {
    // Try to get saved timezone from localStorage
    const savedTimezone = localStorage.getItem('userTimezone');
    if (savedTimezone) {
      return savedTimezone;
    }
    
    // Fallback to browser's timezone
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.warn('Could not detect browser timezone, using UTC');
      return 'UTC';
    }
  });

  const setTimezone = (newTimezone: string) => {
    setTimezoneState(newTimezone);
    localStorage.setItem('userTimezone', newTimezone);
  };

  const value: TimezoneContextType = {
    timezone,
    setTimezone,
    availableTimezones: TIMEZONE_OPTIONS,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export default TimezoneContext;
